import { memo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, User } from 'lucide-react';

interface Reservation {
  id: string;
  customerId: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  staffId: string;
  staffName: string;
  serviceId: string;
  serviceName: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  notes?: string;
  color: string;
  changeHistory?: Array<{ timestamp: string; field: string; oldValue: string; newValue: string }>;
  isVip?: boolean;
  price?: number;
}

interface AppointmentListViewProps {
  appointments: Reservation[];
  onAppointmentClick: (appointment: Reservation) => void;
}


// スタッフごとの色設定
const getStaffColor = (staffId: string) => {
  const staffColors: Record<string, string> = {
    'staff1': '#4CAF50', // 山田 太郎 - グリーン
    'staff2': '#2196F3', // 佐藤 花子 - ブルー
    'staff3': '#FF9800', // 田中 次郎 - オレンジ
    'staff4': '#9C27B0', // 鈴木 美香 - パープル
  };
  return staffColors[staffId] || '#757575'; // デフォルトはグレー
};

const AppointmentListView = memo<AppointmentListViewProps>(({
  appointments,
  onAppointmentClick
}) => {
  // 予約を開始時間順にソート
  const sortedAppointments = [...appointments].sort((a, b) => {
    return a.startTime.localeCompare(b.startTime);
  });

  return (
    <div className="max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-primary/30 scrollbar-track-border/10 hover:scrollbar-thumb-primary/50">
      <div className="flex flex-col gap-1">
        {sortedAppointments.map((appointment) => {
          const staffColor = getStaffColor(appointment.staffId);
          const isCustomStaff = Boolean(appointment.staffId && appointment.staffId !== 'default');

          return (
            <Card
              key={appointment.id}
              data-time={appointment.startTime}
              onClick={() => onAppointmentClick(appointment)}
              className="cursor-pointer rounded-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              style={{
                borderLeft: isCustomStaff ? `4px solid ${staffColor}` : undefined,
                borderColor: isCustomStaff ? `${staffColor}66` : undefined,
                backgroundColor: isCustomStaff ? `${staffColor}14` : undefined,
              }}
            >
              <CardContent className="p-2">
                {/* 時間とティアアイコン */}
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {appointment.startTime}
                    </span>
                  </div>
                </div>

                {/* 顧客名 */}
                <div className="flex items-center gap-1 mb-1">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span
                    className={`text-sm ${isCustomStaff ? 'font-bold' : 'font-medium'}`}
                    style={{ color: isCustomStaff ? staffColor : undefined }}
                  >
                    {appointment.customerName}
                  </span>
                </div>

                {/* サービス名 */}
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-sm text-muted-foreground">
                    {appointment.serviceName}
                  </span>
                </div>

                {/* スタッフと価格 */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {appointment.staffName}
                  </span>
                  {appointment.price && (
                    <span
                      className="text-sm font-medium"
                      style={{ color: isCustomStaff ? staffColor : undefined }}
                    >
                      ¥{appointment.price.toLocaleString()}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        
        {sortedAppointments.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm italic">
              この日の予約はありません
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

AppointmentListView.displayName = 'AppointmentListView';

export default AppointmentListView;